'use client'

import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { AppointmentWithDetails, Client } from '@garage/shared'
import { appointmentInputSchema } from '@garage/shared'
import { Dialog, Segmented, Select, TextField, Button, Autocomplete, useSnackbar, ConfirmDialog } from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { clientDisplayName } from '@garage/shared'

interface AppointmentDialogProps {
  open: boolean
  /** Prefilled date for a new appointment — the day the user clicked. */
  date: string
  appointment?: AppointmentWithDetails
  onClose: () => void
  /** Called after a successful save/delete so the calendar can refetch its range. */
  onSaved: () => void
  readOnly?: boolean
}

type CustomerMode = 'existing' | 'new'

interface FormState {
  customerMode: CustomerMode
  clientId: string | null
  customerName: string
  phone: string
  carId: string | null
  carText: string
  date: string
  time: string | null
  reason: string | null
  notes: string | null
}

export function AppointmentDialog({
  open,
  date,
  appointment,
  onClose,
  onSaved,
  readOnly = false,
}: AppointmentDialogProps) {
  const snackbar = useSnackbar()
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const isCreate = !appointment

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [customerQuery, setCustomerQuery] = useState('')
  const [selectedClient, setSelectedClient] = useState<Client | null>(null)

  const initialState = (): FormState => {
    if (appointment) {
      const mode: CustomerMode = appointment.clientId ? 'existing' : 'new'
      return {
        customerMode: mode,
        clientId: appointment.clientId,
        customerName: appointment.customerLabel,
        phone: appointment.phoneLabel,
        carId: appointment.carId,
        carText: appointment.carLabel,
        date: appointment.date,
        time: appointment.time,
        reason: appointment.reason,
        notes: appointment.notes,
      }
    }
    return {
      customerMode: 'existing',
      clientId: null,
      customerName: '',
      phone: '',
      carId: null,
      carText: '',
      date,
      time: null,
      reason: null,
      notes: null,
    }
  }

  const [formState, setFormState] = useState<FormState>(initialState)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (open) {
      setFormState(initialState())
      setFieldErrors({})
      setShowDeleteConfirm(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, appointment])

  // Fetch clients for autocomplete
  const { data: clientsData } = useQuery({
    queryKey: ['clients-for-appointment-picker', customerQuery],
    queryFn: () => api.clients.list({ q: customerQuery, pageSize: 20 }),
    enabled: open && formState.customerMode === 'existing',
  })

  // Fetch cars for the selected client
  const { data: carsData } = useQuery({
    queryKey: ['cars-for-appointment-picker', formState.clientId],
    queryFn: () => api.cars.list({ clientId: formState.clientId || undefined }),
    enabled: !!formState.clientId,
  })

  const saveMutation = useMutation({
    mutationFn: async (input: ReturnType<typeof appointmentInputSchema.parse>) =>
      isCreate ? api.appointments.create(input) : api.appointments.update(appointment!.id, input),
    onSuccess: () => {
      snackbar.success(isCreate ? t('appointments.appointmentAdded') : t('appointments.appointmentUpdated'))
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
      queryClient.invalidateQueries({ queryKey: qk.dashboard })
      onSaved()
      onClose()
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors)
        snackbar.error(error.message)
      } else {
        snackbar.error(t('common.errorOccurred'))
      }
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.appointments.remove(appointment!.id),
    onSuccess: () => {
      snackbar.success(t('appointments.appointmentDeleted'))
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
      queryClient.invalidateQueries({ queryKey: qk.dashboard })
      onSaved()
      onClose()
    },
    onError: (error) => {
      snackbar.error(error instanceof ApiError ? error.message : t('appointments.failedToDelete'))
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const input = {
      clientId: formState.customerMode === 'existing' ? formState.clientId : null,
      customerName: formState.customerMode === 'new' ? formState.customerName : '',
      phone: formState.phone,
      carId: formState.carId,
      carText: formState.carText,
      date: formState.date,
      time: formState.time,
      reason: formState.reason,
      notes: formState.notes,
    }

    const result = appointmentInputSchema.safeParse(input)
    if (!result.success) {
      const errors: Record<string, string> = {}
      result.error.issues.forEach((issue) => {
        errors[issue.path.join('.')] = issue.message
      })
      setFieldErrors(errors)
      return
    }

    setFieldErrors({})
    saveMutation.mutate(result.data)
  }

  const handleCustomerModeChange = (mode: string) => {
    setFormState((s) => ({
      ...s,
      customerMode: mode as CustomerMode,
      clientId: mode === 'existing' ? s.clientId : null,
      customerName: mode === 'new' ? s.customerName : '',
    }))
    setSelectedClient(null)
    setCustomerQuery('')
  }

  const handleSelectClient = (client: Client) => {
    setSelectedClient(client)
    setFormState((s) => ({
      ...s,
      clientId: client.id,
      customerName: '',
      phone: s.phone || client.phone || '',
      carId: null,
      carText: '',
    }))
    setCustomerQuery(clientDisplayName(client))
  }

  const carOptions = [
    ...(carsData?.map((c) => ({ value: c.id, label: c.plate || c.brand })) ?? []),
    { value: '', label: t('appointments.otherNotListed') },
  ]

  const isExistingCustomer = formState.customerMode === 'existing'

  const canSave = !readOnly
  const canDelete = !readOnly && !isCreate

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        title={
          isCreate
            ? t('appointments.addAppointment')
            : t('appointments.editAppointment')
        }
        size="sm"
        dismissible={false}
        actions={
          <div className="flex gap-3 justify-between w-full">
            {canDelete && (
              <Button
                variant="danger"
                onClick={() => setShowDeleteConfirm(true)}
                loading={deleteMutation.isPending}
                disabled={saveMutation.isPending}
              >
                {t('common.delete')}
              </Button>
            )}
            {!canDelete && <span />}
            <div className="flex gap-3">
              <Button
                variant="outlined"
                onClick={onClose}
                disabled={saveMutation.isPending || deleteMutation.isPending}
              >
                {t('common.cancel')}
              </Button>
              {canSave && (
                <Button
                  variant="filled"
                  onClick={handleSubmit}
                  loading={saveMutation.isPending}
                >
                  {isCreate ? t('common.add') : t('common.save')}
                </Button>
              )}
            </div>
          </div>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Customer Mode Toggle */}
          {!readOnly && (
            <Segmented
              label={t('appointments.customer')}
              options={[
                { value: 'existing', label: t('appointments.existingCustomer') },
                { value: 'new', label: t('appointments.newCustomer') },
              ]}
              value={formState.customerMode}
              onChange={handleCustomerModeChange}
            />
          )}

          {/* Existing Customer Mode */}
          {isExistingCustomer && (
            <>
              <Autocomplete
                label={t('appointments.selectCustomer')}
                value={customerQuery}
                onChange={(text) => setCustomerQuery(text)}
                onSelect={(client: Client) => handleSelectClient(client)}
                fetchOptions={() => Promise.resolve(clientsData?.items ?? [])}
                getOptionLabel={(client: Client) => clientDisplayName(client)}
                getOptionKey={(client: Client) => client.id}
                placeholder={t('appointments.searchCustomer')}
                openOnFocus
                disabled={readOnly}
                error={fieldErrors.clientId || (fieldErrors.customerName ? t('appointments.chooseCustomerOrSwitch') : '')}
              />

              {selectedClient || formState.clientId ? (
                <Select
                  label={t('appointments.car')}
                  value={formState.carId || ''}
                  onChange={(v) =>
                    setFormState((s) => ({
                      ...s,
                      carId: v || null,
                      carText: v ? '' : s.carText,
                    }))
                  }
                  options={carOptions}
                  placeholder={t('appointments.selectCar')}
                  disabled={readOnly}
                  error={fieldErrors.carId}
                  fullWidth
                />
              ) : null}

              {/* Free text car input for "Other / not listed" option */}
              {formState.carId === '' && selectedClient || (formState.clientId && !formState.carId) ? (
                <TextField
                  label={t('appointments.carDescription')}
                  value={formState.carText}
                  onChange={(v) => setFormState((s) => ({ ...s, carText: v }))}
                  error={fieldErrors.carText}
                  placeholder={t('appointments.plateOrDescription')}
                  disabled={readOnly}
                  fullWidth
                />
              ) : null}

              <TextField
                label={t('appointments.phone')}
                type="tel"
                value={formState.phone}
                onChange={(v) => setFormState((s) => ({ ...s, phone: v }))}
                error={fieldErrors.phone}
                disabled={readOnly}
                fullWidth
              />
            </>
          )}

          {/* New Customer Mode */}
          {!isExistingCustomer && (
            <>
              <TextField
                label={t('appointments.customerName')}
                value={formState.customerName}
                onChange={(v) => setFormState((s) => ({ ...s, customerName: v }))}
                error={fieldErrors.customerName}
                disabled={readOnly}
                fullWidth
              />

              <TextField
                label={t('appointments.phone')}
                type="tel"
                value={formState.phone}
                onChange={(v) => setFormState((s) => ({ ...s, phone: v }))}
                error={fieldErrors.phone}
                disabled={readOnly}
                fullWidth
              />

              <TextField
                label={t('appointments.car')}
                value={formState.carText}
                onChange={(v) => setFormState((s) => ({ ...s, carText: v }))}
                error={fieldErrors.carText}
                placeholder={t('appointments.plateOrDescription')}
                disabled={readOnly}
                fullWidth
              />
            </>
          )}

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-3">
            <TextField
              label={t('appointments.date')}
              type="date"
              value={formState.date}
              onChange={(v) => setFormState((s) => ({ ...s, date: v }))}
              error={fieldErrors.date}
              disabled={readOnly}
              fullWidth
            />

            <TextField
              label={t('appointments.time')}
              type="time"
              value={formState.time ?? ''}
              onChange={(v) => setFormState((s) => ({ ...s, time: v || null }))}
              error={fieldErrors.time}
              disabled={readOnly}
              fullWidth
            />
          </div>

          {/* Reason and Notes */}
          <TextField
            label={t('appointments.whatFor')}
            value={formState.reason ?? ''}
            onChange={(v) => setFormState((s) => ({ ...s, reason: v || null }))}
            error={fieldErrors.reason}
            placeholder={t('appointments.reasonOptional')}
            disabled={readOnly}
            fullWidth
          />

          <TextField
            label={t('appointments.notes')}
            value={formState.notes ?? ''}
            onChange={(v) => setFormState((s) => ({ ...s, notes: v || null }))}
            error={fieldErrors.notes}
            multiline
            rows={2}
            placeholder={t('appointments.notesOptional')}
            disabled={readOnly}
            fullWidth
          />

          {/* Linked jobcard info */}
          {!isCreate && appointment.jobcardId && (
            <div className="rounded-md border border-outline-variant bg-surface-high px-3 py-2 text-body-sm">
              <span className="text-on-surface-variant">
                {t('appointments.linkedJobcard')}:{' '}
              </span>
              <a href={`/jobcards/${appointment.jobcardId}`} className="text-primary interactive">
                JC-{appointment.jobcardNumber}
              </a>
            </div>
          )}
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteConfirm}
        title={t('appointments.deleteConfirmTitle')}
        message={t('appointments.deleteConfirmMessage')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        destructive
        onConfirm={() => {
          deleteMutation.mutate()
          setShowDeleteConfirm(false)
        }}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </>
  )
}
